import React, { useState, useEffect, useCallback } from 'react';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import {
  fetchTestSablonlar,
  fetchTestSablon,
  createTestSablon,
  updateTestSablon,
  deleteTestSablon,
  seedVarsayilanArizaSablon,
  runTestSablon,
  fetchTestSablonParcalar,
  fetchTestSablonIslemler,
  fetchTestSablonAktorler,
  fetchTestSablonUrunler,
  fetchTestSablonArizalar,
  fetchTestSablonCihazlar,
  fetchTestSablonChecklist,
  emptySablon,
  emptyRandevuAdim,
  emptySiparisAdim,
  applySablonGarantiToAdimlar,
  inferSablonGaranti,
  canAddRandevu,
  canAddSiparis,
  randevuInheritsParcaFromSiparis,
  ISLEM_TIPLERI,
  isSiparisOnlySablon,
  musteriSelectValue,
  parseMusteriSelectValue,
} from '../services/testSablonService';
import IntegerInput from '../components/IntegerInput';
import { parseAdet } from '../utils/integerInput';
import './SistemeRandevuTestPage.css';

const getErrorMessage = (err, fallback) =>
  err?.message || err?.data?.mesaj || fallback;

const normalizeSablonId = (id) => {
  if (id == null) return '';
  if (typeof id === 'string') return id;
  if (typeof id === 'object' && id.$oid) return String(id.$oid);
  if (typeof id === 'object' && id.buffer) {
    const buf = id.buffer;
    const arr = Array.isArray(buf)
      ? buf
      : Object.keys(buf)
          .sort((a, b) => Number(a) - Number(b))
          .map((k) => buf[k]);
    return arr.map((n) => Number(n).toString(16).padStart(2, '0')).join('');
  }
  if (typeof id === 'object' && typeof id.toString === 'function') {
    const s = id.toString();
    return s && s !== '[object Object]' ? s : '';
  }
  return String(id);
};

const safeStringify = (data) => {
  if (typeof data === 'string') return data;
  const seen = new WeakSet();
  return JSON.stringify(
    data,
    (_key, value) => {
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) return '[Circular]';
        seen.add(value);
      }
      return value;
    },
    2,
  );
};

const normalizeFormFromApi = (s) => {
  const base = emptySablon();
  const islemTipi = s.islemTipi || base.islemTipi;
  const garantiDahilinde = inferSablonGaranti({ ...s, islemTipi });
  const defaultAdimlar =
    islemTipi === 'Sipariş'
      ? [emptySiparisAdim({ garantiDahilinde: false, konsinye: false, standalone: true })]
      : [emptyRandevuAdim(islemTipi, garantiDahilinde)];
  const rawAdimlar =
    Array.isArray(s.adimlar) && s.adimlar.length ? s.adimlar : defaultAdimlar;
  return {
    ...base,
    ...s,
    garantiDahilinde,
    musteri: { ...base.musteri, ...s.musteri },
    konum: { ...base.konum, ...s.konum },
    servisKaydi: { ...base.servisKaydi, ...s.servisKaydi },
    adimlar: applySablonGarantiToAdimlar(rawAdimlar, garantiDahilinde, islemTipi),
  };
};

const SistemeRandevuTestPage = () => {
  const { confirm } = useConfirmDialog();
  const [sablonlar, setSablonlar] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptySablon());
  const [aktorler, setAktorler] = useState({
    adminler: [],
    teknikServisler: [],
    teknisyenler: [],
    musteriler: [],
  });
  const [parcalar, setParcalar] = useState([]);
  const [islemler, setIslemler] = useState([]);
  const [urunler, setUrunler] = useState([]);
  const [arizalar, setArizalar] = useState([]);
  const [cihazlar, setCihazlar] = useState([]);
  const [checklistSoruSayisi, setChecklistSoruSayisi] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [log, setLog] = useState('');

  const appendLog = (title, data) => {
    const line = `\n--- ${title} ---\n${safeStringify(data)}\n`;
    setLog((prev) => (prev + line).slice(-120000));
  };

  const loadList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchTestSablonlar();
      setSablonlar(res?.veri?.sablonlar || []);
    } catch (e) {
      setError(getErrorMessage(e, 'Şablonlar yüklenemedi'));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadYardim = useCallback(async () => {
    try {
      const [aRes, pRes, iRes, uRes, arRes] = await Promise.all([
        fetchTestSablonAktorler(),
        fetchTestSablonParcalar({ limit: 200 }),
        fetchTestSablonIslemler({ limit: 200 }),
        fetchTestSablonUrunler({ limit: 200 }),
        fetchTestSablonArizalar({ limit: 200 }),
      ]);
      setAktorler({
        adminler: aRes?.veri?.adminler || [],
        teknikServisler: aRes?.veri?.teknikServisler || [],
        teknisyenler: aRes?.veri?.teknisyenler || [],
        musteriler: aRes?.veri?.musteriler || [],
      });
      setParcalar(pRes?.veri?.parcalar || []);
      setIslemler(iRes?.veri?.islemler || []);
      setUrunler(uRes?.veri?.urunler || []);
      setArizalar(arRes?.veri?.arizalar || []);
    } catch {
      /* yardım boş kalabilir */
    }
  }, []);

  const loadCihazlar = useCallback(async (musteri) => {
    if (!musteri?.adi || !musteri?.soyadi) {
      setCihazlar([]);
      return;
    }
    try {
      const res = await fetchTestSablonCihazlar(musteri.adi, musteri.soyadi);
      setCihazlar(res?.veri?.cihazlar || []);
    } catch {
      setCihazlar([]);
    }
  }, []);

  const loadChecklistSorulari = useCallback(async (icerikId) => {
    if (!icerikId) {
      setChecklistSoruSayisi(0);
      return;
    }
    try {
      const res = await fetchTestSablonChecklist(icerikId);
      setChecklistSoruSayisi(res?.veri?.toplam || 0);
    } catch {
      setChecklistSoruSayisi(0);
    }
  }, []);

  useEffect(() => {
    loadList();
    loadYardim();
  }, [loadList, loadYardim]);

  useEffect(() => {
    loadCihazlar(form.musteri);
  }, [form.musteri, loadCihazlar]);

  useEffect(() => {
    if (form.islemTipi === 'Kurulum' && form.servisKaydi?.icerikId) {
      loadChecklistSorulari(form.servisKaydi.icerikId);
    } else {
      setChecklistSoruSayisi(0);
    }
  }, [form.islemTipi, form.servisKaydi?.icerikId, loadChecklistSorulari]);

  const selectSablon = async (id) => {
    setSelectedId(id);
    setError('');
    try {
      const res = await fetchTestSablon(id);
      const s = res?.veri?.sablon;
      if (s) setForm(normalizeFormFromApi(s));
    } catch (e) {
      setError(getErrorMessage(e, 'Şablon yüklenemedi'));
    }
  };

  const handleNew = () => {
    setSelectedId(null);
    setForm(emptySablon());
  };

  const handleSeed = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await seedVarsayilanArizaSablon();
      await loadList();
      const id = normalizeSablonId(res?.veri?.sablon?._id);
      if (id) await selectSablon(id);
      appendLog('varsayilan', res);
    } catch (e) {
      setError(getErrorMessage(e, 'Varsayılan şablon oluşturulamadı'));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const wasEdit = Boolean(selectedId);
      let id = selectedId;
      let res;
      if (id) {
        res = await updateTestSablon(id, form);
      } else {
        res = await createTestSablon(form);
      }
      id = normalizeSablonId(res?.veri?.sablon?._id) || id;
      if (id) setSelectedId(id);
      await loadList();
      appendLog(wasEdit ? 'guncelle' : 'kaydet', res);
    } catch (e) {
      setError(getErrorMessage(e, 'Kayıt başarısız'));
    } finally {
      setSaving(false);
    }
  };

  const deleteSablonById = async (id, ad) => {
    const sablonId = normalizeSablonId(id);
    if (!sablonId) return;
    const ok = await confirm({
      title: 'Şablonu sil',
      message: ad
        ? `«${ad}» test şablonu kalıcı olarak silinecek. Emin misiniz?`
        : 'Bu test şablonu kalıcı olarak silinecek. Emin misiniz?',
      confirmText: 'Sil',
      cancelText: 'Vazgeç',
    });
    if (!ok) return;
    setSaving(true);
    setError('');
    try {
      await deleteTestSablon(sablonId);
      if (normalizeSablonId(selectedId) === sablonId) {
        setSelectedId(null);
        setForm(emptySablon());
      }
      await loadList();
      appendLog('sil', { _id: sablonId, ad });
    } catch (e) {
      setError(getErrorMessage(e, 'Silinemedi'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!selectedId) return;
    deleteSablonById(selectedId, form.ad);
  };

  const handleRun = async () => {
    if (!selectedId) {
      setError('Önce şablonu kaydedin');
      return;
    }
    setRunning(true);
    setError('');
    try {
      const res = await runTestSablon(selectedId);
      appendLog('calistir', res);
      if (!res?.basarili) setError(res?.mesaj || 'Betik tamamlanamadı');
    } catch (e) {
      setError(getErrorMessage(e, 'Çalıştırma hatası'));
      appendLog('hata', e?.data || e?.message);
    } finally {
      setRunning(false);
    }
  };

  const updateField = (path, value) => {
    setForm((prev) => {
      const next = { ...prev };
      const keys = path.split('.');
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        cur[keys[i]] = { ...cur[keys[i]] };
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const updateServisKaydi = (patch) => {
    setForm((prev) => ({
      ...prev,
      servisKaydi: { ...prev.servisKaydi, ...patch },
    }));
  };

  const updateAdim = (idx, patch) => {
    setForm((prev) => {
      const adimlar = [...prev.adimlar];
      adimlar[idx] = { ...adimlar[idx], ...patch };
      return { ...prev, adimlar };
    });
  };

  const removeAdim = (idx) => {
    setForm((prev) => ({
      ...prev,
      adimlar: prev.adimlar.filter((_, i) => i !== idx),
    }));
  };

  const addRandevuAdim = () => {
    setForm((prev) => {
      const garanti = prev.garantiDahilinde !== false;
      return {
        ...prev,
        adimlar: applySablonGarantiToAdimlar(
          [...prev.adimlar, emptyRandevuAdim(prev.islemTipi, garanti)],
          garanti,
          prev.islemTipi,
        ),
      };
    });
  };

  const addSiparisAdim = () => {
    setForm((prev) => {
      const siparisOnly = isSiparisOnlySablon(prev);
      const garanti = prev.garantiDahilinde !== false;
      const yeniAdim = siparisOnly
        ? emptySiparisAdim({ garantiDahilinde: false, konsinye: false, standalone: true })
        : emptySiparisAdim(garanti);
      return {
        ...prev,
        adimlar: applySablonGarantiToAdimlar([...prev.adimlar, yeniAdim], garanti, prev.islemTipi),
      };
    });
  };

  const handleIslemTipiChange = (tip) => {
    setForm((prev) => {
      const garanti = tip === 'Kurulum' ? true : prev.garantiDahilinde !== false;
      const adimlar =
        tip === 'Sipariş'
          ? applySablonGarantiToAdimlar(
              [emptySiparisAdim({ garantiDahilinde: false, konsinye: false, standalone: true })],
              garanti,
              'Sipariş',
            )
          : applySablonGarantiToAdimlar([emptyRandevuAdim(tip, garanti)], garanti, tip);
      return {
        ...prev,
        islemTipi: tip,
        garantiDahilinde: garanti,
        adimlar,
      };
    });
  };

  const handleGarantiDahilindeChange = (checked) => {
    setForm((prev) => ({
      ...prev,
      garantiDahilinde: checked,
      adimlar: applySablonGarantiToAdimlar(prev.adimlar, checked, prev.islemTipi),
    }));
  };

  const busy = saving || running;
  const isEditing = Boolean(selectedId);
  const saveLabel = saving
    ? isEditing
      ? 'Güncelleniyor…'
      : 'Kaydediliyor…'
    : isEditing
      ? 'Güncelle'
      : 'Kaydet';

  const sk = form.servisKaydi || {};
  const siparisOnly = isSiparisOnlySablon(form);
  const sablonGaranti = form.garantiDahilinde !== false;
  const addRandevuOk = canAddRandevu(form.adimlar, form.islemTipi);
  const addSiparisOk = canAddSiparis(form.adimlar, form.islemTipi);

  return (
    <div className="srt-page">
      <header className="srt-header">
        <h1 className="srt-title">Sisteme Randevu Test</h1>
        <p className="srt-sub">
          Senaryo tabanlı uçtan uca test: randevu akışları (Kurulum / Bakım / Arıza) veya yalnızca
          fabrika ↔ teknik servis sipariş akışı. Kayıtlar <code>[SRT-TEST]</code> ile işaretlenir.
        </p>
      </header>

      {error ? <div className="srt-alert srt-alert-error">{error}</div> : null}

      <div className="srt-layout">
        <aside className="srt-card srt-list-card">
          <div className="srt-card-head">
            <h2>Şablonlar</h2>
            <div className="srt-card-actions">
              <button type="button" className="srt-btn srt-btn-ghost srt-btn-sm" onClick={handleNew} disabled={busy}>
                Yeni
              </button>
              <button type="button" className="srt-btn srt-btn-secondary srt-btn-sm" onClick={handleSeed} disabled={busy}>
                Varsayılan
              </button>
            </div>
          </div>
          {loading ? (
            <p className="srt-muted">Yükleniyor…</p>
          ) : (
            <ul className="srt-list">
              {sablonlar.map((s) => {
                const id = normalizeSablonId(s._id);
                return (
                  <li key={id} className="srt-list-row">
                    <button
                      type="button"
                      className={`srt-list-item ${selectedId === id ? 'active' : ''}`}
                      onClick={() => selectSablon(id)}
                      disabled={busy}>
                      <span className="srt-list-name">{s.ad}</span>
                      <span className="srt-list-meta">
                        {s.islemTipi || '—'} {!s.aktif ? '· pasif' : ''}
                      </span>
                    </button>
                    <button
                      type="button"
                      className="srt-list-delete"
                      title="Şablonu sil"
                      aria-label={`${s.ad} şablonunu sil`}
                      onClick={() => deleteSablonById(id, s.ad)}
                      disabled={busy}>
                      Sil
                    </button>
                  </li>
                );
              })}
              {sablonlar.length === 0 ? (
                <li className="srt-muted">Henüz şablon yok.</li>
              ) : null}
            </ul>
          )}
        </aside>

        <div className="srt-main">
          <section className="srt-card">
            <h2>Test ortamı</h2>
            {isEditing ? (
              <p className="srt-hint srt-editing-hint">
                Kayıtlı şablon — <strong>Güncelle</strong> ile aynı kayda yazılır.
              </p>
            ) : (
              <p className="srt-hint">Yeni şablon: kaydedin, ardından çalıştırın.</p>
            )}

            <div className="srt-grid">
              <label className="srt-field srt-field-span2">
                <span>Şablon adı</span>
                <input
                  className="srt-input"
                  value={form.ad}
                  onChange={(e) => updateField('ad', e.target.value)}
                  disabled={busy}
                />
              </label>
              <label className="srt-field srt-field-check">
                <input
                  type="checkbox"
                  checked={!!form.aktif}
                  onChange={(e) => updateField('aktif', e.target.checked)}
                  disabled={busy}
                />
                <span>Aktif</span>
              </label>
              <label className="srt-field srt-field-check">
                <input
                  type="checkbox"
                  checked={sablonGaranti}
                  onChange={(e) => handleGarantiDahilindeChange(e.target.checked)}
                  disabled={busy || form.islemTipi === 'Kurulum'}
                />
                <span>Garanti dahilinde</span>
              </label>
            </div>
            {form.islemTipi === 'Kurulum' ? (
              <p className="srt-hint">
                Kurulum şablonlarında garanti her zaman dahildir; tüm randevu ve sipariş adımları buna göre
                çalışır.
              </p>
            ) : (
              <p className="srt-hint">
                {sablonGaranti
                  ? 'Tüm randevu OTP ve sipariş adımları garanti dahilinde işlenir.'
                  : 'Tüm randevu OTP ve sipariş adımları garanti haricinde işlenir (fatura adımları dahil).'}
              </p>
            )}

            <div className="srt-section-title">
              {siparisOnly ? 'Aktörler (fabrika ↔ teknik servis)' : 'Aktörler ve konum'}
            </div>
            {siparisOnly ? (
              <p className="srt-hint srt-field-span2">
                Müşteri, teknisyen ve konum kullanılmaz — yalnızca admin (fabrika) ile teknik servis
                arasında sipariş testi.
              </p>
            ) : null}
            <div className="srt-grid">
              {!siparisOnly ? (
                <label className="srt-field srt-field-span2">
                  <span>Test müşteri</span>
                  <select
                    className="srt-input"
                    value={musteriSelectValue(form.musteri)}
                    onChange={(e) => {
                      const { adi, soyadi } = parseMusteriSelectValue(e.target.value);
                      updateField('musteri', { ...form.musteri, mod: 'sabit', adi, soyadi });
                    }}
                    disabled={busy}>
                    <option value="">Müşteri seçin…</option>
                    {aktorler.musteriler.map((m) => {
                      const val = musteriSelectValue(m);
                      return (
                        <option key={val} value={val}>
                          {m.etiket}
                        </option>
                      );
                    })}
                  </select>
                </label>
              ) : null}
              <label className="srt-field">
                <span>Test admin</span>
                <select
                  className="srt-input"
                  value={form.adminKullaniciAdi || ''}
                  onChange={(e) => updateField('adminKullaniciAdi', e.target.value)}
                  disabled={busy}>
                  <option value="">Seçin…</option>
                  {aktorler.adminler.map((u) => (
                    <option key={u.kullaniciAdi} value={u.kullaniciAdi}>
                      {u.etiket}
                    </option>
                  ))}
                </select>
              </label>
              <label className="srt-field">
                <span>Test teknik servis</span>
                <select
                  className="srt-input"
                  value={form.teknikServisKullaniciAdi || ''}
                  onChange={(e) => updateField('teknikServisKullaniciAdi', e.target.value)}
                  disabled={busy}>
                  <option value="">Seçin…</option>
                  {aktorler.teknikServisler.map((u) => (
                    <option key={u.kullaniciAdi} value={u.kullaniciAdi}>
                      {u.etiket}
                    </option>
                  ))}
                </select>
              </label>
              {!siparisOnly ? (
                <label className="srt-field">
                  <span>Test teknisyen</span>
                  <select
                    className="srt-input"
                    value={form.teknisyenKullaniciAdi || ''}
                    onChange={(e) => updateField('teknisyenKullaniciAdi', e.target.value)}
                    disabled={busy}>
                    <option value="">Seçin…</option>
                    {aktorler.teknisyenler.map((u) => (
                      <option key={u.kullaniciAdi} value={u.kullaniciAdi}>
                        {u.etiket}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              {!siparisOnly ? (
                <>
                  <label className="srt-field">
                    <span>Enlem</span>
                    <input
                      type="number"
                      step="any"
                      className="srt-input"
                      value={form.konum?.latitude ?? ''}
                      onChange={(e) =>
                        updateField('konum', {
                          ...form.konum,
                          latitude: parseFloat(e.target.value) || 0,
                        })
                      }
                      disabled={busy}
                    />
                  </label>
                  <label className="srt-field">
                    <span>Boylam</span>
                    <input
                      type="number"
                      step="any"
                      className="srt-input"
                      value={form.konum?.longitude ?? ''}
                      onChange={(e) =>
                        updateField('konum', {
                          ...form.konum,
                          longitude: parseFloat(e.target.value) || 0,
                        })
                      }
                      disabled={busy}
                    />
                  </label>
                </>
              ) : null}
            </div>

            <div className="srt-section-title">
              {siparisOnly ? 'İşlem tipi' : 'İşlem tipi ve servis kaydı'}
            </div>
            <div className="srt-tip-tabs">
              {ISLEM_TIPLERI.map((tip) => (
                <button
                  key={tip}
                  type="button"
                  className={`srt-tip-tab ${form.islemTipi === tip ? 'active' : ''}`}
                  onClick={() => handleIslemTipiChange(tip)}
                  disabled={busy}>
                  {tip}
                </button>
              ))}
            </div>

            {form.islemTipi === 'Kurulum' ? (
              <div className="srt-grid srt-sk-block">
                <label className="srt-field">
                  <span>Cihaz (ürün)</span>
                  <select
                    className="srt-input"
                    value={sk.urunMod === 'random' ? 'random' : sk.icerikId || ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === 'random') updateServisKaydi({ urunMod: 'random', icerikId: null });
                      else updateServisKaydi({ urunMod: 'secili', icerikId: v });
                    }}
                    disabled={busy}>
                    <option value="random">Rastgele ürün</option>
                    {urunler.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.icerikKodu ? `${u.icerikKodu} — ` : ''}
                        {u.icerikAdi}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="srt-field">
                  <span>Seri numarası</span>
                  <div className="srt-inline">
                    <select
                      className="srt-input"
                      value={sk.seriNoMod || 'random'}
                      onChange={(e) => updateServisKaydi({ seriNoMod: e.target.value })}
                      disabled={busy}>
                      <option value="random">Rastgele</option>
                      <option value="sablon">Sabit (aşağı)</option>
                    </select>
                    {sk.seriNoMod === 'sablon' ? (
                      <input
                        className="srt-input"
                        value={sk.seriNo || ''}
                        onChange={(e) => updateServisKaydi({ seriNo: e.target.value })}
                        placeholder="Seri no"
                        disabled={busy}
                      />
                    ) : null}
                  </div>
                </label>
                <p className="srt-hint srt-field-span2">
                  Kontrol listesi: tüm sorular <strong>evet</strong> olarak işaretlenir
                  {checklistSoruSayisi > 0 ? ` (${checklistSoruSayisi} soru)` : ' (ürün seçildiğinde yüklenir)'}.
                  Adres: müşterinin varsayılan adresi.
                </p>
              </div>
            ) : null}

            {form.islemTipi === 'Bakım' ? (
              <div className="srt-grid srt-sk-block">
                <label className="srt-field srt-field-span2">
                  <span>Cihaz</span>
                  <select
                    className="srt-input"
                    value={
                      sk.cihazMod === 'yok'
                        ? 'yok'
                        : sk.cihazMod === 'random'
                          ? 'random'
                          : sk.cihazId || ''
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === 'random') updateServisKaydi({ cihazMod: 'random', cihazId: null });
                      else if (v === 'yok') updateServisKaydi({ cihazMod: 'yok', cihazId: null });
                      else updateServisKaydi({ cihazMod: 'secili', cihazId: v });
                    }}
                    disabled={busy}>
                    <option value="random">Rastgele cihaz</option>
                    <option value="yok">Cihaz yok</option>
                    {cihazlar.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.etiket}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            ) : null}

            {form.islemTipi === 'Arıza' ? (
              <div className="srt-grid srt-sk-block">
                <label className="srt-field srt-field-span2">
                  <span>Müşteri cihazı</span>
                  <select
                    className="srt-input"
                    value={sk.cihazMod === 'random' ? 'random' : sk.cihazId || ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === 'random') updateServisKaydi({ cihazMod: 'random', cihazId: null });
                      else updateServisKaydi({ cihazMod: 'secili', cihazId: v });
                    }}
                    disabled={busy}>
                    <option value="random">Rastgele cihaz</option>
                    {cihazlar.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.etiket}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="srt-field">
                  <span>Arıza</span>
                  <select
                    className="srt-input"
                    value={sk.arizaMod === 'random' ? 'random' : sk.arizaIds?.[0] || ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === 'random') {
                        updateServisKaydi({ arizaMod: 'random', arizaIds: [] });
                      } else {
                        updateServisKaydi({ arizaMod: 'secili', arizaIds: [v] });
                      }
                    }}
                    disabled={busy}>
                    <option value="random">Rastgele arıza</option>
                    {arizalar.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.icerikKodu ? `${a.icerikKodu} — ` : ''}
                        {a.icerikAdi}
                      </option>
                    ))}
                  </select>
                </label>
                {sk.arizaMod === 'random' ? (
                  <label className="srt-field">
                    <span>Rastgele adet</span>
                    <IntegerInput
                      min={1}
                      className="srt-input"
                      value={sk.arizaAdet ?? 1}
                      onChange={(v) =>
                        updateServisKaydi({ arizaAdet: parseAdet(v, { min: 1 }) })
                      }
                      disabled={busy}
                    />
                  </label>
                ) : null}
              </div>
            ) : null}

            <div className="srt-section-title">Senaryo akışı</div>
            <p className="srt-hint">
              {siparisOnly
                ? 'Fabrika (admin) ↔ teknik servis sipariş akışı: oluştur → onay → kargo → (fatura) → teslim. Birden fazla sipariş adımı eklenebilir.'
                : 'Gerçek hayattaki sıra: randevu → (isteğe bağlı sipariş) → randevu… Kurulum randevularında garanti her zaman dahildir.'}
            </p>

            <div className="srt-timeline">
              {form.adimlar.map((adim, idx) => (
                <div
                  key={`adim-${idx}`}
                  className={`srt-timeline-item srt-timeline-item--${adim.tip}`}>
                  <div className="srt-timeline-badge">{idx + 1}</div>
                  <div className="srt-timeline-body">
                    <div className="srt-timeline-head">
                      <strong>{adim.tip === 'randevu' ? 'Randevu' : 'Sipariş'}</strong>
                      <button
                        type="button"
                        className="srt-btn srt-btn-ghost srt-btn-sm"
                        onClick={() => removeAdim(idx)}
                        disabled={busy}>
                        Kaldır
                      </button>
                    </div>

                    {adim.tip === 'randevu' ? (
                      <RandevuAdimEditor
                        adim={adim}
                        idx={idx}
                        islemTipi={form.islemTipi}
                        templateGaranti={sablonGaranti}
                        inheritsParcaFromSiparis={randevuInheritsParcaFromSiparis(form.adimlar, idx)}
                        islemler={islemler}
                        parcalar={parcalar}
                        busy={busy}
                        onChange={(patch) => updateAdim(idx, patch)}
                      />
                    ) : (
                      <SiparisAdimEditor
                        adim={adim}
                        idx={idx}
                        parcalar={parcalar}
                        busy={busy}
                        standalone={siparisOnly}
                        templateGaranti={sablonGaranti}
                        onChange={(patch) => updateAdim(idx, patch)}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="srt-timeline-actions">
              {!siparisOnly ? (
                <button
                  type="button"
                  className="srt-btn srt-btn-secondary"
                  onClick={addRandevuAdim}
                  disabled={busy || !addRandevuOk}
                  title={!addRandevuOk ? 'Son randevuda «Yeni randevu gerekli» işaretli değil' : ''}>
                  + Randevu ekle
                </button>
              ) : null}
              <button
                type="button"
                className="srt-btn srt-btn-secondary"
                onClick={addSiparisAdim}
                disabled={busy || !addSiparisOk}
                title={
                  !addSiparisOk
                    ? siparisOnly
                      ? ''
                      : 'Önce siparisGerekli işaretli bir randevu OTP adımı gerekli'
                    : ''
                }>
                + Sipariş ekle
              </button>
            </div>

            <div className="srt-actions">
              <button type="button" className="srt-btn srt-btn-primary" onClick={handleSave} disabled={busy}>
                {saveLabel}
              </button>
              <button
                type="button"
                className="srt-btn srt-btn-accent"
                onClick={handleRun}
                disabled={busy || !selectedId}>
                {running ? 'Çalışıyor…' : 'Şablonu çalıştır'}
              </button>
              {isEditing ? (
                <button type="button" className="srt-btn srt-btn-danger" onClick={handleDelete} disabled={busy}>
                  Sil
                </button>
              ) : null}
            </div>
          </section>

          <section className="srt-card srt-log-card">
            <div className="srt-card-head">
              <h2>Çıktı</h2>
              <button type="button" className="srt-btn srt-btn-ghost srt-btn-sm" onClick={() => setLog('')}>
                Temizle
              </button>
            </div>
            <pre className="srt-log">{log || 'Betik çalıştırıldığında adım adım JSON burada görünür.'}</pre>
          </section>
        </div>
      </div>
    </div>
  );
};

function RandevuAdimEditor({
  adim,
  islemTipi,
  templateGaranti,
  inheritsParcaFromSiparis,
  islemler,
  parcalar,
  busy,
  onChange,
}) {
  const otp = adim.otp || {};
  const kurulum = islemTipi === 'Kurulum';
  const garantiEtiket = kurulum || templateGaranti ? 'Garanti dahilinde' : 'Garanti haricinde';

  const patchOtp = (p) => onChange({ otp: { ...otp, ...p } });
  const patchKatalog = (rows) => onChange({ katalogIslemler: rows });
  const patchIlave = (rows) => onChange({ ilaveIslemler: rows });
  const patchParca = (rows) => onChange({ parcaKullanimlari: rows });

  return (
    <div className="srt-adim-inner">
      <div className="srt-grid">
        <label className="srt-field">
          <span>Gün offset</span>
          <input
            type="number"
            min={1}
            className="srt-input"
            value={adim.gunOffset ?? 1}
            onChange={(e) => onChange({ gunOffset: parseInt(e.target.value, 10) || 1 })}
            disabled={busy}
          />
        </label>
        <label className="srt-field">
          <span>Saat</span>
          <input
            className="srt-input"
            value={adim.saat || '09:00'}
            onChange={(e) => onChange({ saat: e.target.value })}
            disabled={busy}
          />
        </label>
      </div>

      <RowList
        title="Katalog işlem"
        rows={adim.katalogIslemler || []}
        busy={busy}
        onAdd={() =>
          patchKatalog([...(adim.katalogIslemler || []), { mod: 'random', icerikId: null }])
        }
        onRemove={(i) => patchKatalog((adim.katalogIslemler || []).filter((_, j) => j !== i))}
        renderRow={(row, i) => (
          <KatalogRow
            row={row}
            islemler={islemler}
            busy={busy}
            onChange={(patch) => {
              const rows = [...(adim.katalogIslemler || [])];
              rows[i] = { ...rows[i], ...patch };
              patchKatalog(rows);
            }}
          />
        )}
      />

      <RowList
        title="İlave işlem"
        rows={adim.ilaveIslemler || []}
        busy={busy}
        onAdd={() =>
          patchIlave([
            ...(adim.ilaveIslemler || []),
            { mod: 'sabit', islemAdi: '', fiyat: 10, adet: 1 },
          ])
        }
        onRemove={(i) => patchIlave((adim.ilaveIslemler || []).filter((_, j) => j !== i))}
        renderRow={(row, i) => (
          <div className="srt-row-line">
            <select
              className="srt-input"
              value={row.mod || 'sabit'}
              onChange={(e) => {
                const rows = [...(adim.ilaveIslemler || [])];
                rows[i] = { ...rows[i], mod: e.target.value };
                patchIlave(rows);
              }}
              disabled={busy}>
              <option value="sabit">Sabit ad</option>
              <option value="random">Rastgele ad (sentetik)</option>
            </select>
            {row.mod !== 'random' ? (
              <input
                className="srt-input"
                placeholder="İşlem adı"
                value={row.islemAdi || ''}
                onChange={(e) => {
                  const rows = [...(adim.ilaveIslemler || [])];
                  rows[i] = { ...rows[i], islemAdi: e.target.value };
                  patchIlave(rows);
                }}
                disabled={busy}
              />
            ) : null}
            <input
              type="number"
              className="srt-input srt-input-narrow"
              value={row.fiyat ?? 10}
              onChange={(e) => {
                const rows = [...(adim.ilaveIslemler || [])];
                rows[i] = { ...rows[i], fiyat: parseFloat(e.target.value) || 0 };
                patchIlave(rows);
              }}
              disabled={busy}
            />
          </div>
        )}
      />

      {inheritsParcaFromSiparis && !(adim.parcaKullanimlari || []).length ? (
        <p className="srt-hint srt-parca-inherit-hint">
          Parça kullanımı (OTP): önceki <strong>Sipariş</strong> adımındaki{' '}
          <strong>Kullanılacak (sonraki randevu)</strong> adetleri otomatik uygulanır; burada parça
          eklemeniz gerekmez. İsterseniz aşağıdan manuel satır ekleyerek siparişi geçersiz kılabilirsiniz.
        </p>
      ) : null}
      <RowList
        title={
          inheritsParcaFromSiparis
            ? 'Parça kullanımı (OTP) — isteğe bağlı override'
            : 'Parça kullanımı (OTP)'
        }
        rows={adim.parcaKullanimlari || []}
        busy={busy}
        onAdd={() =>
          patchParca([...(adim.parcaKullanimlari || []), { mod: 'random', icerikId: null, adet: 1 }])
        }
        onRemove={(i) => patchParca((adim.parcaKullanimlari || []).filter((_, j) => j !== i))}
        renderRow={(row, i) => (
          <ParcaRow
            row={row}
            parcalar={parcalar}
            busy={busy}
            adetLabel="Kullanılacak adet"
            onChange={(patch) => {
              const rows = [...(adim.parcaKullanimlari || [])];
              rows[i] = { ...rows[i], ...patch };
              patchParca(rows);
            }}
          />
        )}
      />

      <div className="srt-otp-flags">
        <p className="srt-hint srt-muted-sm">{garantiEtiket} (şablon ayarı)</p>
        <label className="srt-check">
          <input
            type="checkbox"
            checked={!!otp.yeniRandevuGerekli}
            onChange={(e) =>
              patchOtp({
                yeniRandevuGerekli: e.target.checked,
                siparisGerekli: e.target.checked ? otp.siparisGerekli : false,
              })
            }
            disabled={busy}
          />
          Yeni randevu gerekli
        </label>
        <label className="srt-check">
          <input
            type="checkbox"
            checked={!!otp.siparisGerekli}
            onChange={(e) => patchOtp({ siparisGerekli: e.target.checked })}
            disabled={busy || !otp.yeniRandevuGerekli}
          />
          Sipariş gerekli
        </label>
        {kurulum ? (
          <label className="srt-check">
            <input
              type="checkbox"
              checked={otp.ilkCalistirmaYapildi !== false}
              onChange={(e) => patchOtp({ ilkCalistirmaYapildi: e.target.checked })}
              disabled={busy}
            />
            İlk çalıştırma yapıldı
          </label>
        ) : null}
      </div>

      {kurulum ? (
        <label className="srt-field">
          <span>Kurulum OTP seri no</span>
          <select
            className="srt-input"
            value={adim.kurulumOtp?.seriNoKaynak || 'sablon'}
            onChange={(e) =>
              onChange({ kurulumOtp: { seriNoKaynak: e.target.value } })
            }
            disabled={busy}>
            <option value="sablon">Şablondaki seri</option>
            <option value="random">Yeni rastgele seri</option>
          </select>
        </label>
      ) : null}
    </div>
  );
}

function SiparisAdimEditor({ adim, parcalar, busy, standalone, templateGaranti, onChange }) {
  const isKonsinye = adim.konsinye === true;
  const satirlar = adim.satirlar || [];
  const admin = adim.admin || {};

  const patchSatirlar = (rows) => onChange({ satirlar: rows });
  const patchAdmin = (p) => onChange({ admin: { ...admin, ...p } });

  const showFatura = standalone ? !templateGaranti || isKonsinye : !templateGaranti;

  return (
    <div className="srt-adim-inner">
      {standalone ? (
        templateGaranti ? (
          <p className="srt-hint">
            Garanti dahilinde sipariş (şablon) — servis kaydı gerektirir; yalnız sipariş akışında
            çalışmayabilir.
          </p>
        ) : (
          <label className="srt-field srt-field-span2">
            <span>Sipariş tipi</span>
            <select
              className="srt-input"
              value={isKonsinye ? 'konsinye' : 'dogrudan'}
              onChange={(e) => {
                const konsinye = e.target.value === 'konsinye';
                onChange({
                  konsinye,
                  garantiDahilinde: false,
                });
              }}
              disabled={busy}>
              <option value="dogrudan">Doğrudan sipariş (fabrika → teknik servis)</option>
              <option value="konsinye">Konsinye sipariş</option>
            </select>
          </label>
        )
      ) : (
        <p className="srt-hint srt-muted-sm">
          {templateGaranti ? 'Garanti dahilinde' : 'Garanti haricinde'} (şablon ayarı)
          {templateGaranti ? ' — fatura adımı çalıştırmada atlanır.' : ''}
        </p>
      )}
      {standalone && isKonsinye ? (
        <p className="srt-hint">Konsinye sipariş — stok konsinye katmanına yazılır; fatura gerekir.</p>
      ) : null}

      <RowList
        title="Sipariş satırları"
        rows={satirlar}
        busy={busy}
        onAdd={() =>
          patchSatirlar([
            ...satirlar,
            { parcaMod: 'random', icerikId: null, siparisAdet: 1, kullanilacakAdet: 1 },
          ])
        }
        onRemove={(i) => patchSatirlar(satirlar.filter((_, j) => j !== i))}
        renderRow={(row, i) => (
          <ParcaRow
            row={{
              mod: row.parcaMod,
              icerikId: row.icerikId,
              adet: row.siparisAdet,
            }}
            parcalar={parcalar}
            busy={busy}
            modKey="parcaMod"
            adetLabel="Sipariş adedi"
            adetMin={1}
            kullanilacakAdet={row.kullanilacakAdet ?? 1}
            onKullanilacakChange={
              standalone
                ? undefined
                : (val) => {
                    const rows = [...satirlar];
                    rows[i] = { ...rows[i], kullanilacakAdet: val };
                    patchSatirlar(rows);
                  }
            }
            onChange={(patch) => {
              const rows = [...satirlar];
              rows[i] = {
                ...rows[i],
                parcaMod: patch.parcaMod ?? patch.mod,
                icerikId: patch.icerikId,
                siparisAdet: patch.adet ?? rows[i].siparisAdet,
              };
              patchSatirlar(rows);
            }}
          />
        )}
      />

      <div className="srt-grid">
        <label className="srt-field">
          <span>Kargo firması</span>
          <input
            className="srt-input"
            value={admin.kargoFirmasi || ''}
            onChange={(e) => patchAdmin({ kargoFirmasi: e.target.value })}
            disabled={busy}
          />
        </label>
        {showFatura ? (
          <label className="srt-field">
            <span>Fatura bilgisi</span>
            <input
              className="srt-input"
              value={admin.faturaBilgisi || ''}
              onChange={(e) => patchAdmin({ faturaBilgisi: e.target.value })}
              placeholder="Boş = otomatik"
              disabled={busy}
            />
          </label>
        ) : null}
      </div>
    </div>
  );
}

function RowList({ title, rows, busy, onAdd, onRemove, renderRow }) {
  return (
    <div className="srt-row-block">
      <div className="srt-row-block-head">
        <span>{title}</span>
        <button type="button" className="srt-btn srt-btn-ghost srt-btn-sm" onClick={onAdd} disabled={busy}>
          + Ekle
        </button>
      </div>
      {rows.length === 0 ? <p className="srt-muted-sm">Satır yok</p> : null}
      {rows.map((row, i) => (
        <div key={`${title}-${i}`} className="srt-row-wrap">
          {renderRow(row, i)}
          <button
            type="button"
            className="srt-btn srt-btn-ghost srt-btn-sm"
            onClick={() => onRemove(i)}
            disabled={busy}>
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

function KatalogRow({ row, islemler, busy, onChange }) {
  return (
    <div className="srt-row-line">
      <select
        className="srt-input"
        value={row.mod === 'random' ? 'random' : row.icerikId || ''}
        onChange={(e) => {
          const v = e.target.value;
          if (v === 'random') onChange({ mod: 'random', icerikId: null });
          else onChange({ mod: 'secili', icerikId: v });
        }}
        disabled={busy}>
        <option value="random">Rastgele işlem</option>
        {islemler.map((x) => (
          <option key={x.id} value={x.id}>
            {x.icerikKodu ? `${x.icerikKodu} — ` : ''}
            {x.icerikAdi}
          </option>
        ))}
      </select>
    </div>
  );
}

function ParcaRow({
  row,
  parcalar,
  busy,
  modKey = 'mod',
  adetLabel,
  adetMin = 0,
  onChange,
  kullanilacakAdet,
  onKullanilacakChange,
}) {
  const modField = modKey || 'mod';
  const minAdet = Number.isFinite(adetMin) ? adetMin : 0;
  const gridClass = onKullanilacakChange
    ? 'srt-siparis-satir'
    : adetLabel
      ? 'srt-parca-grid srt-parca-grid--with-adet'
      : 'srt-parca-grid';

  const parcaSelect = (
    <select
      className="srt-input"
      value={row[modField] === 'random' ? 'random' : row.icerikId || ''}
      onChange={(e) => {
        const v = e.target.value;
        if (v === 'random') onChange({ [modField]: 'random', icerikId: null });
        else onChange({ [modField]: 'secili', icerikId: v });
      }}
      disabled={busy}>
      <option value="random">Rastgele parça</option>
      {parcalar.map((p) => (
        <option key={p.id} value={p.id}>
          {p.icerikKodu ? `${p.icerikKodu} — ` : ''}
          {p.icerikAdi}
        </option>
      ))}
    </select>
  );

  return (
    <div className={gridClass}>
      <label className="srt-field">
        <span>Parça</span>
        {parcaSelect}
      </label>
      {adetLabel ? (
        <label className="srt-field srt-field-adet">
          <span>{adetLabel}</span>
          <IntegerInput
            min={minAdet}
            className="srt-input"
            value={row.adet ?? minAdet}
            onChange={(v) => onChange({ adet: parseAdet(v, { min: minAdet, fallback: minAdet }) })}
            disabled={busy}
          />
        </label>
      ) : null}
      {onKullanilacakChange ? (
        <label className="srt-field srt-field-adet srt-field-adet-wide">
          <span>Kullanılacak (sonraki randevu)</span>
          <IntegerInput
            min={0}
            className="srt-input"
            value={kullanilacakAdet ?? 1}
            onChange={(v) => onKullanilacakChange(parseAdet(v, { min: 0, fallback: 0 }))}
            disabled={busy}
          />
        </label>
      ) : null}
    </div>
  );
}

export default SistemeRandevuTestPage;
